import type { ReactNode } from 'react';
import { Modal } from '@/components/ui/modal';
import { Button } from '@/components/ui/button';

export interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  description: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
  // When true, disables the confirm button independently of isLoading.
  // Used by TypeToConfirmDialog to gate the action behind a name-typing
  // guard for irreversible operations.
  disabled?: boolean;
  // Visual treatment for the confirm button. Defaults to 'danger' so existing
  // delete-style callers don't need to opt in. Use 'primary' for cautionary
  // confirmations that aren't destructive (e.g. reassigning a meter to a
  // different unit) — the action is intentional but not harmful.
  variant?: 'danger' | 'primary' | 'secondary';
}

export function ConfirmDialog({
  isOpen,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
  isLoading,
  disabled,
  variant = 'danger',
}: ConfirmDialogProps) {
  return (
    <Modal isOpen={isOpen} onClose={onCancel} title={title} size="sm">
      <div className="text-sm text-gray-600 dark:text-gray-400 mb-6 space-y-2">{description}</div>
      <div className="flex gap-3">
        <Button type="button" variant="outline" onClick={onCancel} className="flex-1" disabled={isLoading}>
          {cancelLabel}
        </Button>
        <Button
          type="button"
          variant={variant}
          onClick={onConfirm}
          isLoading={isLoading}
          disabled={disabled}
          className="flex-1"
        >
          {confirmLabel}
        </Button>
      </div>
    </Modal>
  );
}
